import React, { useEffect } from 'react';
import { InputField, InputFieldProps, NumberField } from '../../atomic/forms';
import { useTenant } from '../../../contexts/TenantContext';
import { KintoneApp } from '../../../models/TenantModel';
import {
    getTenantForKintoneAppFields,
    getTenantOptions,
    putTenantOptions,
} from '../../../APIs/tenants';
import { DialogBox } from '../../atomic/dialog';
import { Loading } from '../../atomic/effects';
import { Link, useNavigate } from 'react-router';
import { AdminKintoneTable } from './AdminKintoneTable';
import { safeUrl } from '../../../utils/safeUrl';
import { Icon, Icons } from '../../atomic/icons';

export const AdminKintoneApps: React.FC = () => {
    const [showConfirm, setShowConfirm] = React.useState<boolean>(false);
    const [confirmMessage, setConfirmMessage] = React.useState<string>('');

    const [loading, setLoading] = React.useState<boolean>(false);

    const { tenantId } = useTenant();
    const [userId, setUserId] = React.useState<string>('');
    const [password, setPassword] = React.useState<string>('');
    const [endPoint, setEndPoint] = React.useState<string>('');
    const [apps, setApps] = React.useState<KintoneApp[]>([]);
    const [removeId, setRemoveId] = React.useState<number>(0);

    const [appId, setAppId] = React.useState<string>('');

    const [showAddAppDialog, setShowAddAppDialog] =
        React.useState<boolean>(false);

    interface InputRef {
        appId: React.MutableRefObject<InputField<InputFieldProps>>;
    }
    const refs: InputRef = ['appId'].reduce((ret, key) => {
        ret[key] = React.useRef<InputField>();
        return ret;
    }, {} as InputRef);

    const resetForm = () => {
        setAppId('');
        setConfirmMessage('');
    };

    useEffect(() => {
        resetForm();
    }, []);

    useEffect(() => {
        if (tenantId) {
            getTenantOptions({
                tenantId: tenantId,
                category: 'kintone',
                keys: ['id'],
                array: ['apps'],
            })
                .then((resp) => {
                    if (resp.data) {
                        if (
                            resp.data.kintone?.id ||
                            resp.data.kintone?.pass ||
                            resp.data.kintone?.endpoint
                        ) {
                            setUserId(resp.data.kintone?.id ?? '');
                            setPassword(resp.data.kintone?.pass ?? '');
                            setEndPoint(resp.data.kintone?.endpoint ?? '');
                            setApps(resp.data.kintone?.apps ?? []);
                        } else {
                            navigate('/app/admin/kintone/');
                        }
                    }
                })
                .catch((err) => {
                    console.warn(err);
                });
        }
    }, [tenantId]);

    const openConfirmDialog = React.useCallback(() => {
        const _app = apps.some((app) => {
            return Number(app.appId) === Number(appId);
        });
        if (_app) {
            navigate(safeUrl`/app/admin/kintone/app/${appId}/edit`);
        } else {
            setConfirmMessage('Kintoneアプリを追加しますか？');
            setShowConfirm(true);
        }
    }, [apps, appId]);

    const navigate = useNavigate();
    const updateTenantOption = (dialog: DialogBox, _app: KintoneApp[]) => {
        putTenantOptions(tenantId, {
            kintone: {
                apps: _app,
            },
        })
            .then((resp) => {
                if (resp.status === 200) {
                    setShowConfirm(false);
                    resetForm();
                    navigate(safeUrl`/app/admin/kintone/app/${appId}/edit`);
                } else {
                    console.warn(resp);
                    dialog.addError(
                        resp.message || 'kintone連携情報の更新に失敗しました'
                    );
                }
            })
            .catch((err) => {
                console.warn(err);
                dialog.addError(
                    err.response?.data?.message ||
                        err.message ||
                        'kintone連携情報の更新に失敗しました'
                );
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onConfirmUpdateOK = React.useCallback(
        (dialog: DialogBox) => {
            setLoading(true);
            getTenantForKintoneAppFields(tenantId, Number(appId))
                .then((resp) => {
                    if (resp.success) {
                        updateTenantOption(dialog, [
                            {
                                appId: Number(appId),
                                appDescription: '',
                                fields: [],
                            },
                        ]);
                    } else {
                        console.warn(resp);
                        dialog.addError('kintoneアプリ連携に失敗しました');
                    }
                })
                .catch((err) => {
                    console.warn(err);
                    dialog.addError(
                        err.response?.data?.message ||
                            err.message ||
                            'kintoneアプリ連携に失敗しました'
                    );
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [appId]
    );
    const onConfirmCancel = React.useCallback(() => {
        setShowConfirm(false);
    }, []);

    const onConfirmRemoveAppOK = React.useCallback(
        (dialog: DialogBox) => {
            const _apps = apps
                .filter((app: KintoneApp) => {
                    return app.appId == removeId;
                })
                .map((app) => {
                    app.fields = null;
                    return app;
                });

            setLoading(true);
            putTenantOptions(tenantId, {
                kintone: {
                    apps: _apps,
                },
            })
                .then((resp) => {
                    if (resp.status === 200) {
                        const _apps = apps
                            .filter((app: KintoneApp) => {
                                return app.appId !== removeId;
                            })
                            .map((app) => {
                                return app;
                            });
                        setApps(_apps);
                        setRemoveId(0);
                    } else {
                        console.warn(resp);
                        dialog.addError(
                            resp?.message,
                            'アプリ設定の削除に失敗しました'
                        );
                    }
                })
                .catch((err) => {
                    console.warn(err);
                    dialog.addError(err, 'アプリ設定の削除に失敗しました');
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [removeId]
    );

    const onConfirmRemoveAppCancel = React.useCallback(() => {
        setRemoveId(0);
    }, []);

    const onRemoveApp = (removeAppId: number) => {
        setRemoveId(removeAppId);
    };

    return (
        <>
            {tenantId && userId && (
                <div className="app-main admin-kintone">
                    <h2>Kintoneアプリ設定</h2>
                    <div className="menu">
                        <ul>
                            <li>
                                <Link
                                    to="#"
                                    onClick={() => setShowAddAppDialog(true)}
                                >
                                    <Icon icon={Icons.Add} />
                                    アプリの追加
                                </Link>
                            </li>
                            <li>
                                <Link to="/app/admin/kintone/">
                                    <Icon icon={Icons.Config} />
                                    Kintone連携アカウントの管理
                                </Link>
                            </li>
                            <li>
                                <Link to="/app/admin/kintone/logs">
                                    <Icon icon={Icons.History} />
                                    ログ一覧
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div className="app-list">
                        <AdminKintoneTable
                            apps={apps}
                            setApps={setApps}
                            onRemoveApp={onRemoveApp}
                        />
                    </div>

                    {showConfirm && (
                        <DialogBox
                            title="確認"
                            onOK={onConfirmUpdateOK}
                            onCancel={onConfirmCancel}
                        >
                            {confirmMessage}
                        </DialogBox>
                    )}
                    {removeId != 0 && (
                        <DialogBox
                            title="確認"
                            onOK={onConfirmRemoveAppOK}
                            onCancel={onConfirmRemoveAppCancel}
                        >
                            アプリID：{removeId} の設定を削除しますか？
                        </DialogBox>
                    )}
                    {showAddAppDialog && (
                        <DialogBox
                            title="アプリの追加"
                            onOK={() => {
                                setShowAddAppDialog(false);
                                openConfirmDialog();
                            }}
                            onCancel={() => setShowAddAppDialog(false)}
                        >
                            <div className="edit-forms">
                                <div className="form">
                                    <ul className="fields">
                                        <li>
                                            <NumberField
                                                label="アプリの追加編集"
                                                _onChange={setAppId}
                                                value={appId}
                                                required={true}
                                                autoComplete="off"
                                                //ref={refs.appId}
                                                placeholder="アプリID"
                                            />
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </DialogBox>
                    )}
                    {loading && <Loading />}
                </div>
            )}
        </>
    );
};
